import { Router } from '@angular/router';
import { TagsProvider } from '../../provider/tags.provider';
import { PersonsProvider } from '../../provider/persons.provider';
import { ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    AumicoCoreProvider,
    FIRST_ELEMENT_INDEX,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NotificationType,
    OkCancelDialogActions,
    USER_INPUT_DEBOUNCE_TIME,
} from '@modeso/aumico-lib-core-fe';
import { CompaniesErrors, FileMetaData, ICompanyDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ILegalFormDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Observable } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { GetUserDto } from '../../../../../aumico-lib-users-fe/src/public-api';
import { localizationKeys } from '../../constants/localizationKeys';
import { TasksUpdate } from '../../enums/tasks-update.enum';
import { CompaniesProvider } from '../../provider/companies.provider';
import { AumicoCompaniesService } from '../../services/companies.service';

export enum DeleteState {
    IDLE = 0,
    CANCELED = 1,
    COMPLETED = 2,
    IN_PROGRESS = 3,
}
@UntilDestroy()
@Component({
    selector: 'aumico-edit-company-component',
    templateUrl: 'edit-company.component.html',
    styleUrls: ['edit-company.component.scss'],
})
export class EditCompanyComponent implements OnInit, OnChanges {
    local = localizationKeys;

    countries: INameId[] = [];
    active: boolean;
    @Input() logo: File;
    fileMetadata: FileMetaData | undefined;

    @Input() workflowOptions: INameId[] = [];

    private _organizationHasCpmmActive: boolean = false;
    @Input() set organizationHasCpmmActive(value: boolean) {
        this._organizationHasCpmmActive = value;
        this.updateCpmmValidators();
    }
    get organizationHasCpmmActive(): boolean {
        return this._organizationHasCpmmActive;
    }

    private _organizationHasMandateAndTaskManagementEnabled: boolean = false;
    @Input() set organizationHasMandateAndTaskManagementEnabled(value: boolean) {
        this._organizationHasMandateAndTaskManagementEnabled = value;
        this.preselectWorkflowIfNeeded();
    }
    get organizationHasMandateAndTaskManagementEnabled(): boolean {
        return this._organizationHasMandateAndTaskManagementEnabled;
    }



    @Input() set legalFormOptions(value: INameId[]) {
        if (value != null) {
            this.legalForms = cloneDeep(value);
            this.updateCompanyFormGroup();
        }
    }

    @Input() set originalLegalFormOptions(value: ILegalFormDTO[]) {
        if (value != null) {
            this.originalLegalForms = cloneDeep(value);
        }
    }

    @Input() set countryOptions(countries: INameId[]) {
        this.countries = [...countries];
    }

    industries: INameId[] = [];
    @Input() set industryOptions(industryOptions: INameId[]) {
        this.industries = industryOptions;
        this.initializeCompanyFormGroup();
    }

    @Input() hideStatus: boolean;
    @Input() currentLanguage = 'de-ch';
    @Input() defaultLanguage = 'de-ch';

    @Input() proceedButtonEvent$: Observable<void>;

    @Input() save$: Observable<void>;

    @Input() defaultCountry: INameId | undefined;

    @Input() set activeCompany(company: ICompanyDTO | undefined) {
        if (company != null) {
            this.editCompanyHandler(company);
        }
    }

    @Input() assignedUser: GetUserDto;

    @Input() clerkUser: GetUserDto;

    _organizationUsers: INameId[] = [];
    @Input() set organizationUsers(value: INameId[]) {
        if (value != null) {
            this._organizationUsers = value;
            this.updateCompanyFormGroup();
        }
    }
    get organizationUsers(): INameId[] {
        return this._organizationUsers;
    }

    @Output() createdCompany = new EventEmitter<ICompanyDTO>();
    @Output() isFormValid = new EventEmitter<boolean>();

    @Output() activeCompanyOutput = new EventEmitter<ICompanyDTO>();
    @Output() editMode = new EventEmitter<boolean>();
    @Output() countrySelectionChange = new EventEmitter<string>();

    @Output() fileUploaded = new EventEmitter<File>();

    @Input() deleteInProgress = false;
    @Output() deletingStateChange = new EventEmitter<DeleteState>();

    legalForms: INameId[] = [];
    originalLegalForms: ILegalFormDTO[] = [];
    companyFormGroup: UntypedFormGroup;
    editableMode = false;
    public isDeleting = false;

    private companyToSave: ICompanyDTO | undefined;
    savedCompany: ICompanyDTO | undefined;

    private tasksUpdateSelection: TasksUpdate | undefined;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private companiesProvider: CompaniesProvider,
        private readonly tagsProvider: TagsProvider,
        private readonly personsProvider: PersonsProvider,
        private readonly router: Router,
        private coreProvider: AumicoCoreProvider,
        private dialog: MatDialog,
        private companiesService: AumicoCompaniesService,
    ) { }

    /** Resolved by the hosting page; visibility only — the backend enforces the rule. */
    @Input() canManageFx = false;

    fxBusy = false;

    /** Create mode: the terms were accepted, the effect activates once the company exists. */
    private fxPendingActivation = false;

    onFxPendingActivationChange(pending: boolean): void {
        this.fxPendingActivation = pending;
    }

    onFxActivate(): void {
        this.callFxEndpoint(this.companiesService.activateFx(this.savedCompany?._id ?? ''));
    }

    onFxDeactivate(): void {
        this.callFxEndpoint(this.companiesService.deactivateFx(this.savedCompany?._id ?? ''));
    }

    private callFxEndpoint(request$: Observable<ICompanyDTO>): void {
        if (this.savedCompany?._id == null || this.fxBusy) {
            return;
        }
        this.fxBusy = true;
        request$.pipe(untilDestroyed(this)).subscribe({
            next: (updated) => {
                this.fxBusy = false;
                this.savedCompany = updated;
            },
            error: (error: HttpErrorResponse) => {
                this.fxBusy = false;
                this.fireErrorNotification(error);
            },
        });
    }

    // REV-22155: tags of the organization and the persons linked to this company.
    tags: ITagDTO[] = [];
    tagUsage: { id: string; usage: ITagUsageDTO } | null = null;
    tagError: HttpErrorResponse | null = null;
    linkedPersons: { _id: string; name: string; role: string }[] = [];

    ngOnInit(): void {
        this.initError();
        this.initTagsAndLinkedPersons();
        this.proceedButtonEvent$.pipe(untilDestroyed(this)).subscribe(() => this.createOrEditCompany());

        this.companiesProvider.getCompaniesError$().pipe(untilDestroyed(this)).subscribe();

        this.save$.pipe(untilDestroyed(this)).subscribe(() => this.createOrEditCompany());
    }
    /** REV-22155: tag box of the form, and "Verknüpfte Personen" in the profile. */
    private initTagsAndLinkedPersons(): void {
        this.tagsProvider
            .getTags$()
            .pipe(untilDestroyed(this))
            .subscribe((tags) => (this.tags = tags ?? []));
        this.tagsProvider
            .getTagUsage$()
            .pipe(untilDestroyed(this))
            .subscribe((usage) => (this.tagUsage = usage));
        // Without this a failed tag call stayed silent and the box kept waiting.
        this.tagsProvider
            .getTagsError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe((error: HttpErrorResponse) => {
                this.tagError = error;
                this.fireErrorNotification(error);
                this.tagsProvider.resetTagsErrors();
            });
        this.tagsProvider.dispatchTagsList();

        this.personsProvider
            .getPersonsOfCompany$()
            .pipe(untilDestroyed(this))
            .subscribe((persons) => {
                // One entry per LINK: the same person can hold several roles at this company
                // (e.g. Inhaber/in and Verwaltungsrat) and every one of them must be visible.
                // Inactive persons are shown like the others, without a mark (§8).
                this.linkedPersons = (persons ?? []).flatMap((person) =>
                    (person.companies ?? [])
                        .filter((link) => link.companyId === this.savedCompany?._id)
                        .map((link) => ({
                            _id: person._id,
                            // "Nachname Vorname", as everywhere else.
                            name: `${person.lastName} ${person.firstName}`.trim(),
                            role: link.role,
                        })),
                );
            });
    }

    onTagCreate(name: string): void {
        this.tagsProvider.dispatchCreateTag(name);
    }

    onTagDeleteRequest(tagId: string): void {
        this.tagsProvider.dispatchTagUsage(tagId);
    }

    onTagDeleteConfirm(tagId: string): void {
        this.tagsProvider.dispatchDeleteTag(tagId);
    }

    onTagDeleteCancel(): void {
        this.tagsProvider.dispatchClearTagUsage();
    }

    /** `/<lang>/home/companies/…` → `/<lang>/home/persons/<id>/profile`; keeps the lib free of the app's paths. */
    onLinkedPersonClick(personId: string): void {
        const [lang, home] = this.router.url.split('?')[0].split('/').filter(Boolean);
        this.router.navigate([`/${lang}/${home}/persons`, personId, 'profile']);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultCountry'] != null && changes['defaultCountry'].currentValue != null) {
            const defaultCountry = changes['defaultCountry'].currentValue as INameId;
            this.setDefaultCountry(defaultCountry);
        }

        if (changes['deleteInProgress'] != null) {
            if (changes['deleteInProgress'].currentValue) {
                this.startDeleteOperation();
            } else {
                this.cancelDeleteOperation();
            }
        }

        if (changes['workflowOptions']?.currentValue != null) {
            this.preselectWorkflowIfNeeded();
        }
    }
    onCountrySelectionChange(countryCode: string): void {
        this.countrySelectionChange.emit(countryCode);
    }

    onLogoUploaded($event: File): void {
        this.logo = $event;
        this.fileUploaded.emit($event);
    }

    onTasksUpdateSelection(tasksUpdate: TasksUpdate | undefined): void {
        this.tasksUpdateSelection = tasksUpdate;
    }

    private setDefaultCountry(countryToSet: INameId): void {
        if (this.savedCompany && this.savedCompany.country) {
            return;
        }

        const country = this.countries.find((country) => country.id === countryToSet.id);
        let selectCountry: INameId | null = null;

        if (country != null) {
            selectCountry = country;
        }
        this.companyFormGroup.get('country').setValue(selectCountry);
    }

    private notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value != null && control.value.toString().trim() === '') {
            return { required: true };
        }
        return null;
    }

    private initializeCompanyFormGroup(): void {
        this.companyFormGroup = this.formBuilder.group({
            name: new UntypedFormControl(null, [Validators.required, this.notEmptyValidator]),
            label: new UntypedFormControl(null),
            street: new UntypedFormControl(null),
            additionToAddress: new UntypedFormControl(null),
            zip: new UntypedFormControl(null),
            city: new UntypedFormControl(null),
            country: new UntypedFormControl(null, [Validators.required]),
            email: new UntypedFormControl(null, [Validators.email]),
            phone: new UntypedFormControl(null),
            UID: new UntypedFormControl(null),
            legalform: new UntypedFormControl(null, [Validators.required]),
            taxDomicile: new UntypedFormControl(null),
            GICSCode: new UntypedFormControl(this.industries[FIRST_ELEMENT_INDEX], [Validators.required]),
            firstName: new UntypedFormControl(''),
            lastName: new UntypedFormControl(''),
            assignee: new UntypedFormControl(null, this.organizationHasCpmmActive ? [Validators.required] : []),
            clerk: new UntypedFormControl(null, this.organizationHasCpmmActive ? [Validators.required] : []),
            assignedOn: new UntypedFormControl(null),
            assignedOnClerk: new UntypedFormControl(null),
            workflow: new UntypedFormControl(this.organizationHasMandateAndTaskManagementEnabled ? null : this.workflowOptions[FIRST_ELEMENT_INDEX], this.organizationHasCpmmActive ? [Validators.required] : []),
            zefix_uid: new UntypedFormControl(null),
            zefix_linked: new UntypedFormControl(false),
            zefix_last_synced_at: new UntypedFormControl(null),
            zefix_overridden_fields: new UntypedFormControl(null),
            // REV-22155: tag ids; flows into the DTO through the spread in the mapper.
            tags: new UntypedFormControl([]),
        });

        // Apply CPMM validators after form creation
        this.updateCpmmValidators();
        this.monitorCompanyFormChanges();
    }

    private updateCpmmValidators(): void {
        if (!this.companyFormGroup) {
            return;
        }

        const assigneeControl = this.companyFormGroup.get('assignee');
        const workflowControl = this.companyFormGroup.get('workflow');
        const clerkControl = this.companyFormGroup.get('clerk');

        if (this.organizationHasCpmmActive) {
            assigneeControl?.setValidators([Validators.required]);
            clerkControl?.setValidators([Validators.required]);
            workflowControl?.setValidators([Validators.required]);
        } else {
            assigneeControl?.setValidators([]);
            clerkControl?.setValidators([]);
            workflowControl?.setValidators([]);
        }

        assigneeControl?.updateValueAndValidity();
        workflowControl?.updateValueAndValidity();
        clerkControl?.updateValueAndValidity();
    }

    private preselectWorkflowIfNeeded(): void {
        if (this.companyFormGroup != null && this.organizationHasMandateAndTaskManagementEnabled === false && this.companyFormGroup.get('workflow').value == null) {
            this.companyFormGroup.get('workflow').setValue(this.workflowOptions[FIRST_ELEMENT_INDEX]);
        }
    }

    private monitorCompanyFormChanges(): void {
        this.companyFormGroup.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                tap((company) => {
                    const mappedValue = this.mapCompanyFormGroupValueToCompanyDTO(company);
                    if (fastDeepEqual(this.companyToSave?.country, mappedValue.country) === false) {
                        this.countrySelectionChange.emit(mappedValue.country?.id);
                    }
                    this.companyToSave = mappedValue;
                }),
                untilDestroyed(this),
            )
            .subscribe(() => {
                // Skip validation during delete
                if (this.isDeleting) {
                    return;
                }

                this.createdCompany.emit(this.companyToSave);
                this.isFormValid.emit(this.companyFormGroup.valid);
            });
    }

    private updateCompanyFormGroup(): void {
        if (this.savedCompany == null) {
            return;
        }

        const savedCompanyFormGroupValue = {
            ...this.savedCompany,
            GICSCode: this.industries.find((industry) => industry.id === this.savedCompany.GICSCode),
            legalform: this.legalForms.find((legalForm) => legalForm.id === this.savedCompany.legalform?._id) ?? null,
            assignee: this.organizationUsers.find((user) => user.id === this.savedCompany.assignee),
            clerk: this.organizationUsers.find((user) => user.id === this.savedCompany.clerk),
            workflow: this.savedCompany.workflowId,
            zefix_overridden_fields: this.savedCompany.zefix_overridden_fields ?? null,
        };
        this.companyFormGroup.patchValue(savedCompanyFormGroupValue, { emitEvent: false });
    }

    private initError(): void {
        this.companiesProvider
            .getCompaniesError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe((error: HttpErrorResponse) => {
                // Skip error notifications during delete operations
                if (this.isDeleting) {
                    return;
                }
                this.fireErrorNotification(error);
            });
    }

    private fireErrorNotification(companiesError: HttpErrorResponse): void {
        const errorMessageLocalizationKey = this.getErrorMessageLocalizationKey(companiesError);
        const errorTitleLocalizationKey = localizationKeys.aumico_reports_notification_error_title;
        this.coreProvider.dispatchAddNotification(
            errorTitleLocalizationKey,
            errorMessageLocalizationKey,
            NotificationType.ERROR,
        );
    }

    private getErrorMessageLocalizationKey(companiesError: HttpErrorResponse): string {
        if (companiesError.error.message === CompaniesErrors.COMPANY_NAME_EXISTS) {
            return localizationKeys.aumico_companies_form_validation_enter_company_name_exists;
        }

        return localizationKeys.aumico_companies_notification_error_description;
    }

    private editCompanyHandler(company: ICompanyDTO) {
        this.savedCompany = company;
        // REV-22155: only in the profile — "+ Neue Firma" has no company to link persons to.
        this.personsProvider.dispatchPersonsOfCompany(company._id);
        this.fileMetadata = company?.logo;
        this.active = company.active;
        this.editableMode = true;
        this.updateCompanyFormGroup();
    }

    private createOrEditCompany(): void {
        if (!this.companyFormGroup.valid) {
            this.coreProvider.dispatchAddNotification(
                'aumico_reports_notification_error_title',
                'aumico_companies_form_validation_error',
                NotificationType.ERROR,
            );
            this.companyFormGroup.markAllAsTouched();
            this.isFormValid.emit(false);
            return;
        }
        if (this.editableMode) {
            this.companyToSave._id = this.savedCompany._id;
            const legalFormChanged = this.isLegalFormSelectionChanged();
            if (!legalFormChanged) {
                this.companiesProvider.dispatchUpdateCompany(this.companyToSave, this.logo, this.tasksUpdateSelection);
                return;
            }

            const dialogRef = this.openLegalFormChangeConfirmationDialog();

            dialogRef
                .afterClosed()
                .pipe(untilDestroyed(this))
                .subscribe((result) => {
                    if (result === OkCancelDialogActions.OK) {
                        this.companiesProvider.dispatchUpdateCompany(
                            this.companyToSave,
                            this.logo,
                            this.tasksUpdateSelection,
                        );
                    }

                    dialogRef.close();
                });
        } else {
            this.companiesProvider.createCompany(this.companyToSave, this.logo, this.fxPendingActivation);
        }
    }

    private mapCompanyFormGroupValueToCompanyDTO(companyFormValue): ICompanyDTO {

        const selectedLegalForm = this.originalLegalForms.find((legalForm) => legalForm._id === companyFormValue.legalform?.id);

        return {
            ...companyFormValue,
            GICSCode: companyFormValue.GICSCode?.id,
            legalform: selectedLegalForm ?? null,
            assignee: companyFormValue.assignee?.id,
            clerk: companyFormValue.clerk?.id,
            workflowId: companyFormValue.workflow?.id,
        };
    }

    private isLegalFormSelectionChanged(): boolean {
        if (this.savedCompany == null || this.companyToSave == null) {
            return false;
        }

        return this.savedCompany.legalform?._id !== this.companyToSave.legalform?._id;
    }

    private openLegalFormChangeConfirmationDialog(): MatDialogRef<ModalComponent, OkCancelDialogActions> {
        return this.dialog.open<ModalComponent, ModalData, OkCancelDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            width: '400px',
            data: {
                showCloseButton: false,
                titleIcon: ModalTitleIcons.DEFAULT,
                footerButtons: [
                    {
                        localizationKey: 'aumico_reports_general_info_company_update_confirm',
                        action: OkCancelDialogActions.OK,
                        disabled: false,
                        buttonColor: 'blue',
                    },
                    {
                        localizationKey: 'aumico_reports_general_info_company_update_cancel',
                        action: OkCancelDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                footerBackgroundColor: 'white',
                content: {
                    text: ['aumico_reports_company_legal_form_update_confirmation_text'],
                },
                width: '400px',
                minHeight: '216px',
                textSize: '16px',
            },
        });
    }

    public startDeleteOperation(): void {
        // Set delete state to disable form validation and async operations
        this.isDeleting = true;
        this.deletingStateChange.emit(DeleteState.IN_PROGRESS);

        // Cancel any pending form validation or async operations by resetting form monitoring
        // This will prevent interference with the delete operation
        this.companyFormGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            // During delete, form changes should not trigger validation
        });
    }

    public cancelDeleteOperation(): void {
        // Reset delete state and restore normal form behavior
        this.isDeleting = false;
        this.deletingStateChange.emit(DeleteState.CANCELED);
        this.monitorCompanyFormChanges();
    }
}
